import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import AppFooter from '../components/AppFooter';

// Why the previous session ended. 'user' is deliberately absent: someone who
// clicked "Sign out" does not need to be told they signed out.
const SIGN_OUT_MESSAGES = {
  inactivity: 'You were signed out due to inactivity.',
  expired: 'Your session expired. Please sign in again.',
};

function LoginPage() {
  const { authenticated, loading, login, refresh, signOutReason, connectionError } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Where RequireAuth wanted to send them before the redirect.
  const destination = location.state?.from || '/';

  if (loading) return <div className="card">Loading…</div>;
  if (authenticated) return <Navigate to={destination} replace />;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      // No navigate() here: `authenticated` flips and the <Navigate> above runs.
      // Doing both is how you get a double navigation and a flash of the wrong
      // page.
    } catch (err) {
      // The backend deliberately returns one generic message for every failure
      // mode, so there is nothing more specific to show and nothing to add.
      setError(err.response?.data?.detail || 'Could not sign in. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <form className="card login-card" onSubmit={handleSubmit}>
        {/* "Mints" in green -- var(--success), the app's real green token,
            legible against this card's light background. Sidebar.js keeps the
            name plain: its dark teal panel already leans on --teal-300 for
            emphasis (the accent rule, the active nav pill), and a second green
            there would compete with it rather than read as a brand mark. */}
        <h1>📋 Docu-<span style={{ color: 'var(--success)' }}>Mints</span></h1>
        <p className="login-subtitle">Sign in to continue</p>

        {/* A first /me that never got an answer lands the visitor here looking
            exactly like a signed-out one. Say which it is, and offer the retry
            rather than making them guess that reloading is the fix. */}
        {connectionError && (
          <div className="alert alert-warning" role="status">
            Can't reach the server. It may be starting up.{' '}
            <button type="button" className="btn btn-secondary btn-small" onClick={() => refresh()}>
              Retry
            </button>
          </div>
        )}

        {/* Landing here used to be silent, so an idle logout was indistinguishable
            from the app losing your session for no reason. */}
        {!error && !connectionError && SIGN_OUT_MESSAGES[signOutReason] && (
          <div className="alert alert-warning" role="status">{SIGN_OUT_MESSAGES[signOutReason]}</div>
        )}

        {error && <div className="alert alert-error" role="alert">{error}</div>}

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        <button type="submit" className="btn btn-primary login-submit" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <AppFooter className="app-footer--login" />
    </div>
  );
}

export default LoginPage;
